import * as React from 'react';
import { Star, Clock, Search, ChevronsUpDown, X, Check, ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import type { ProjectWithMetadata, ProjectFilters } from '@/types/project-selector';
import { useProjectSelector } from '@/hooks/use-project-selector';
import { ProjectSearchModal } from './ProjectSearchModal';
import type { Project } from '@/types';

interface ProjectSelectorProps {
  projects: Project[] | ProjectWithMetadata[];
  value?: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When false, the Favourites section is hidden in both the dropdown and the full search modal */
  showFavourites?: boolean;
  // Server-side search props
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
  required?: boolean;
  isSubmitted?: boolean;
  /** Company ID to scope favourites and recently used projects per company */
  companyId?: string;
  fallbackName?: string;
  fallbackCode?: string;
}

export function ProjectSelector({
  projects,
  value,
  onChange,
  disabled,
  placeholder = 'Select project...',
  showFavourites = true,
  onServerSearch,
  isSearching,
  required,
  isSubmitted,
  companyId,
  fallbackName,
  fallbackCode,
}: ProjectSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const popoverContentRef = React.useRef<HTMLDivElement>(null);

  const {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite,
  } = useProjectSelector(projects, companyId);

  const selectedProject = enrichedProjects.find((p) => p.id === value);

  // Filter projects based on search query
  const filteredProjects = React.useMemo(() => {
    if (!searchQuery.trim()) return enrichedProjects;
    const query = searchQuery.toLowerCase();
    return enrichedProjects.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.code.toLowerCase().includes(query) ||
        ('description' in p && p.description && p.description.toLowerCase().includes(query))
    );
  }, [enrichedProjects, searchQuery]);

  const filteredFavourites = React.useMemo(() => {
    if (!searchQuery.trim()) return favouriteProjects.slice(0, 5);
    return favouriteProjects.filter((p) => filteredProjects.some((fp) => fp.id === p.id));
  }, [favouriteProjects, filteredProjects, searchQuery]);

  const filteredRecent = React.useMemo(() => {
    if (!searchQuery.trim()) return recentProjects.slice(0, 5);
    return recentProjects.filter((p) => filteredProjects.some((fp) => fp.id === p.id));
  }, [recentProjects, filteredProjects, searchQuery]);

  const additionalProjects = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    const favouriteIds = new Set(filteredFavourites.map((p) => p.id));
    const recentIds = new Set(filteredRecent.map((p) => p.id));
    return filteredProjects
      .filter((p) => !favouriteIds.has(p.id) && !recentIds.has(p.id))
      .slice(0, 10);
  }, [filteredProjects, filteredFavourites, filteredRecent, searchQuery]);

  const hasNoResults =
    searchQuery &&
    filteredFavourites.length === 0 &&
    filteredRecent.length === 0 &&
    additionalProjects.length === 0;

  const hasNoDefault =
    !searchQuery &&
    filteredRecent.length === 0 &&
    (showFavourites ? filteredFavourites.length === 0 : true);

  // Handle project selection
  const handleSelectProject = React.useCallback(
    (projectId: string) => {
      const project = enrichedProjects.find((p) => p.id === projectId);
      const isArchived = 'status' in project! && project.status === 'archived';
      const isInactive = 'active' in project! && project.active === false &&
        (!('status' in project!) || !['active', 'at-risk', 'on-hold', 'planning', 'pending'].includes(String(project.status).toLowerCase()));
      if (isArchived || isInactive) {
        alert('Cannot select archived/inactive project. Please choose an active project.');
        return;
      }
      onChange(projectId);
      recordProjectAccess(projectId);
      setDropdownOpen(false);
    },
    [enrichedProjects, onChange, recordProjectAccess]
  );

  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setDropdownOpen(false);
    setSearchQuery('');
  }, []);

  // Trigger server-side search when the inline dropdown search query changes
  // This ensures projects beyond the initial page can be found in the dropdown
  React.useEffect(() => {
    if (!onServerSearch || !dropdownOpen) return;

    const timer = setTimeout(() => {
      onServerSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, onServerSearch, dropdownOpen]);

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery('');
      // Reset server-side search to restore the full project list
      if (onServerSearch) {
        onServerSearch('');
      }
    }
  }, [dropdownOpen, onServerSearch]);

  const triggerLabel = selectedProject
    ? `${selectedProject.name} (${selectedProject.code})`
    : fallbackName
      ? `${fallbackName}${fallbackCode ? ` (${fallbackCode})` : ''}`
      : '';

  return (
    <>
      <Popover
        open={!disabled && dropdownOpen}
        onOpenChange={(open) => !disabled && setDropdownOpen(open)}
      >
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-expanded={dropdownOpen}
            aria-disabled={disabled || undefined}
            aria-label="Select project"
            className={cn(
              'border-input flex h-9 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
              'cursor-pointer hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              disabled && 'cursor-not-allowed opacity-50 pointer-events-none',
              required && !value && !isSubmitted && 'border-destructive focus-visible:ring-destructive'
            )}
            onClick={() => !disabled && setDropdownOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                !disabled && setDropdownOpen(true);
              }
              if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                !disabled && handleOpenModal();
              }
            }}
            tabIndex={disabled ? -1 : 0}
          >
            <span className={cn('flex-1 truncate', !triggerLabel && 'text-muted-foreground')}>
              {triggerLabel || placeholder}
            </span>
            <div className="flex items-center gap-1 shrink-0 ml-1">
              {selectedProject && !disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange('');
                  }}
                  className="text-muted-foreground hover:text-foreground rounded-sm p-0.5"
                  aria-label="Clear selection"
                  tabIndex={-1}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '300px' }}
          align="start"
          ref={popoverContentRef}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => {
              const input = popoverContentRef.current?.querySelector('input');
              input?.focus();
            }, 0);
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            {/* Search input */}
            <CommandInput
              placeholder="Search by code, name, client, or PM..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />

            <CommandList className="max-h-[360px]">
              {/* Empty states */}
              {hasNoResults && (
                <CommandEmpty>
                  <div className="py-6 text-center">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No projects found for "{searchQuery}"</p>
                    <p className="text-xs mt-1 text-muted-foreground">Try a different search term</p>
                  </div>
                </CommandEmpty>
              )}
              {hasNoDefault && !hasNoResults && (
                <CommandEmpty>
                  <span className="text-muted-foreground">
                    {showFavourites
                      ? 'No favourites or recent projects'
                      : 'No recently used projects'}
                  </span>
                </CommandEmpty>
              )}

              {/* Favourites */}
              {showFavourites && filteredFavourites.length > 0 && (
                <CommandGroup
                  heading={
                    <span className="flex items-center gap-1.5">
                      <Star className="h-3.5 w-3.5" />
                      FAVOURITES
                    </span>
                  }
                >
                  {filteredFavourites.map((project) => (
                    <CommandItem
                      key={project.id}
                      value={`fav-${project.id}`}
                      onSelect={() => handleSelectProject(project.id)}
                      className="gap-2"
                    >
                      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
                      <span className="flex-1 truncate">
                        {project.name}{' '}
                        <span className="text-muted-foreground">({project.code})</span>
                      </span>
                      {value === project.id && (
                        <Check className="h-4 w-4 shrink-0" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Recently Used */}
              {filteredRecent.length > 0 && (
                <>
                  {showFavourites && filteredFavourites.length > 0 && <CommandSeparator />}
                  <CommandGroup
                    heading={
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        RECENTLY USED
                      </span>
                    }
                  >
                    {filteredRecent.map((project) => (
                      <CommandItem
                        key={project.id}
                        value={`recent-${project.id}`}
                        onSelect={() => handleSelectProject(project.id)}
                        className="gap-2"
                      >
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">({project.code})</span>
                        </span>
                        {value === project.id && (
                          <Check className="h-4 w-4 shrink-0" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* Other search results */}
              {searchQuery && additionalProjects.length > 0 && (
                <>
                  {(filteredFavourites.length > 0 || filteredRecent.length > 0) && (
                    <CommandSeparator />
                  )}
                  <CommandGroup
                    heading={
                      <span className="flex items-center gap-1.5">
                        <Search className="h-3.5 w-3.5" />
                        OTHER RESULTS
                      </span>
                    }
                  >
                    {additionalProjects.map((project) => (
                      <CommandItem
                        key={project.id}
                        value={`other-${project.id}`}
                        onSelect={() => handleSelectProject(project.id)}
                        className="gap-2"
                      >
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">({project.code})</span>
                        </span>
                        {value === project.id && (
                          <Check className="h-4 w-4 shrink-0" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {/* Searching indicator */}
              {searchQuery && isSearching && (
                <CommandEmpty>
                  <div className="py-6 text-center">
                    <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin opacity-30" />
                    <p>Searching...</p>
                  </div>
                </CommandEmpty>
              )}
            </CommandList>

            {/* Show More footer */}
            <Separator />
            <div className="p-1">
              <Button
                variant="ghost"
                className="w-full justify-center text-sm gap-1.5"
                onClick={handleOpenModal}
              >
                <ArrowRight className="h-4 w-4" />
                Show More...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Full Search Modal */}
      <ProjectSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        projects={
          enrichedProjects.map((p) => ({
            ...p,
            status:
              'status' in p
                ? String(p.status).toLowerCase()
                : p.active
                ? 'active'
                : 'archived',
            description: 'description' in p ? p.description : undefined,
          })) as ProjectWithMetadata[]
        }
        favourites={
          favouriteProjects.map((p) => ({
            ...p,
            status:
              'status' in p
                ? String(p.status).toLowerCase()
                : p.active
                ? 'active'
                : 'archived',
            description: 'description' in p ? p.description : undefined,
          })) as ProjectWithMetadata[]
        }
        onToggleFavourite={toggleFavourite}
        value={value}
        onSelect={handleSelectProject}
        onServerSearch={onServerSearch}
        isSearching={isSearching}
      />
    </>
  );
}
